import React, { useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Table } from 'antd';
import Loader from 'theme/icons/loader';
import EmptyData from 'theme/empty-data';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const WrapLoader = styled.div`
  width: 50px;
  height: 50px;
  animation: ${rotate} 2s linear infinite;
  color: ${themeGet('colors.primary_500')};
`;

const StyledTable = styled(Table)`
  .ant-table {
    box-sizing: border-box;
    color: ${themeGet('colors.grey_600')};
    line-height: 1.5715;
    font-feature-settings: 'tnum';
    position: relative;
    z-index: 0;
    font-size: 14px;
    margin: 0px;
    padding: 0px;
    font-variant: tabular-nums;
    list-style: none;
    background: rgb(255, 255, 255);
    border-radius: 2px;
  }

  .ant-table table {
    width: 100%;
    text-align: left;
    border-collapse: separate;
    border-radius: 2px 2px 0px 0px;
    border-spacing: 0px;
  }

  .ant-table-header {
    border-bottom: 2px solid ${themeGet('colors.primary_100')};
  }

  .ant-table-thead > tr > th {
    padding: ${themeGet('space.s')}px;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 22px;
    overflow-wrap: break-word;
    color: ${themeGet('colors.grey_600')};
    border-bottom: 2px solid ${themeGet('colors.primary_100')};
    background-color: white;
  }

  .ant-table-cell-fix-left,
  .ant-table-cell-fix-right {
    z-index: 2;
    position: sticky !important;
    background: ${themeGet('colors.white')};
  }

  .ant-table-cell-fix-left-first::after,
  .ant-table-cell-fix-left-last::after {
    right: 0px;
    transform: translateX(100%);
  }

  .ant-table-cell-fix-left-first::after,
  .ant-table-cell-fix-left-last::after,
  .ant-table-cell-fix-right-first::after,
  .ant-table-cell-fix-right-last::after {
    position: absolute;
    top: 0px;
    bottom: -1px;
    width: 30px;
    content: '';
    pointer-events: none;
    transition: box-shadow 0.3s ease 0s, -webkit-box-shadow 0.3s ease 0s;
  }

  .ant-table-ping-left .ant-table-cell-fix-left-first::after,
  .ant-table-ping-left .ant-table-cell-fix-left-last::after,
  .ant-table-ping-left:not(.ant-table-has-fix-left) .ant-table-container::before {
    box-shadow: rgba(0, 0, 0, 0.15) 10px 0px 8px -8px inset;
  }

  .ant-table-cell-fix-right-first:after,
  .ant-table-cell-fix-right-last:after {
    left: 0;
    transform: translateX(-100%);
  }

  .ant-table-ping-right .ant-table-cell-fix-right-first:after,
  .ant-table-ping-right .ant-table-cell-fix-right-last:after,
  .ant-table-ping-right:not(.ant-table-has-fix-right) .ant-table-container:after {
    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.15);
  }

  .ant-table-column-sorters {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0;
  }

  .ant-table-column-sorter {
    margin-left: 8px;
  }

  .ant-table-column-sorter-inner {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
  }

  .ant-table-thead th.ant-table-column-has-sorters {
    cursor: pointer;
    padding: ${themeGet('space.s')}px;
    transition: all 0.3s ease 0s;
  }

  .ant-table-column-sorter-down,
  .ant-table-column-sorter-up {
    display: inline-block;
    font-size: 16px;
    color: ${themeGet('colors.grey_200')};
  }

  .ant-table-tbody > tr > td,
  .ant-table-thead > tr > th,
  .ant-table tfoot > tr > td,
  .ant-table tfoot > tr > th {
    position: relative;
    overflow-wrap: break-word;
  }

  .ant-table-tbody > tr {
    height: 98px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 2px solid ${themeGet('colors.primary_100')};
    padding: ${themeGet('space.xs')}px ${themeGet('space.s')}px;
    height: 98px;

    & > img.table-image {
      width: 100%;
    }
  }

  .ant-tooltip-inner {
    min-width: 30px;
    min-height: 32px;
    color: rgb(255, 255, 255);
    text-align: left;
    overflow-wrap: break-word;
    background-color: rgba(0, 0, 0, 0.75);
    box-shadow: rgba(0, 0, 0, 0.12) 0px 3px 6px -4px, rgba(0, 0, 0, 0.08) 0px 6px 16px 0px,
      rgba(0, 0, 0, 0.05) 0px 9px 28px 8px;
    padding: 6px 8px;
    text-decoration: none;
    border-radius: 2px;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background-color: ${themeGet('colors.primary_100')};
    ${({ onRow }) => (onRow ? 'cursor: pointer' : '')};
  }

  .ant-table-pagination.ant-pagination {
    margin: ${themeGet('space.s')}px ${themeGet('space.s')}px;
    justify-content: center;
  }

  .ant-table-pagination {
    display: flex;
  }

  .ant-pagination,
  .ant-pagination ol,
  .ant-pagination ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .ant-pagination {
    box-sizing: border-box;
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    font-variant: tabular-nums;
    line-height: 1.5715;
    font-feature-settings: 'tnum';
  }

  .ant-pagination-disabled,
  .ant-pagination-disabled:focus,
  .ant-pagination-disabled:hover {
    cursor: not-allowed;
  }

  .ant-pagination-next,
  .ant-pagination-prev {
    outline: 0;
  }

  .ant-pagination-jump-next,
  .ant-pagination-jump-prev,
  .ant-pagination-next,
  .ant-pagination-prev {
    display: inline-block;
    min-width: 32px;
    height: 32px;
    color: rgba(0, 0, 0, 0.85);
    line-height: 32px;
    text-align: center;
    vertical-align: middle;
    list-style: none;
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.3s;
  }

  .ant-pagination-item {
    min-width: 32px;
    text-align: center;
    list-style: none;
    background-color: ${themeGet('colors.white')};
    border: 1px solid ${themeGet('colors.grey_200')};
    border-radius: 2px;
    outline: 0;
    cursor: pointer;
    user-select: none;
  }

  .ant-pagination-item-active {
    font-weight: 500;
    background: ${themeGet('colors.white')};
    border-color: ${themeGet('colors.primary_500')};
    color: ${themeGet('colors.primary_500')};
  }

  .ant-pagination-item-active a {
    font-weight: 500;
    background: ${themeGet('colors.white')};
    border-color: ${themeGet('colors.primary_500')};
    color: ${themeGet('colors.primary_500')};
  }

  .ant-pagination-item,
  .ant-pagination-total-text {
    display: inline-block;
    height: 32px;
    margin-right: ${themeGet('space.xs')}px;
    line-height: 30px;
    vertical-align: middle;
  }

  .ant-pagination-jump-next,
  .ant-pagination-jump-prev,
  .ant-pagination-prev {
    margin-right: ${themeGet('space.xs')}px;
  }

  .ant-pagination-options {
    display: inline-block;
    margin-left: ${themeGet('space.s')}px;
    vertical-align: middle;
  }

  .ant-pagination-options-size-changer.ant-select {
    display: inline-block;
    width: auto;
  }

  .ant-select {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    font-variant: tabular-nums;
    line-height: 1.5715;
    list-style: none;
    font-feature-settings: 'tnum';
    position: relative;
    display: inline-block;
    cursor: pointer;
  }

  .ant-pagination-next .ant-pagination-item-link,
  .ant-pagination-prev .ant-pagination-item-link {
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    font-size: 12px;
    text-align: center;
    background-color: ${themeGet('colors.white')};
    border: 1px solid ${themeGet('colors.grey_200')};
    border-radius: 2px;
    outline: none;
    transition: all 0.3s;
  }

  .ant-pagination-next button,
  .ant-pagination-prev button {
    color: rgba(0, 0, 0, 0.85);
    cursor: pointer;
    user-select: none;
  }

  .ant-pagination-disabled .ant-pagination-item-link,
  .ant-pagination-disabled:focus .ant-pagination-item-link,
  .ant-pagination-disabled:hover .ant-pagination-item-link {
    color: rgba(0, 0, 0, 0.25);
    border-color: ${themeGet('colors.grey_100')};
    cursor: not-allowed;
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    position: relative;
    background-color: ${themeGet('colors.white')};
    border: 1px solid ${themeGet('colors.grey_200')};
    border-radius: 2px;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  }

  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    width: 100%;
    height: 32px;
    padding: 0 ${themeGet('space.s')}px;
  }

  .ant-select-single .ant-select-selector {
    display: flex;
  }

  .ant-select-arrow {
    display: inline-block;
    font-style: normal;
    text-transform: none;
    vertical-align: -0.125em;
    text-rendering: optimizeLegibility;
    position: absolute;
    top: 53%;
    right: 11px;
    width: 12px;
    height: 12px;
    margin-top: -6px;
    color: rgba(0, 0, 0, 0.25);
    font-size: 12px;
    line-height: 1;
    text-align: center;
    pointer-events: none;
  }

  .ant-select-single.ant-select-show-arrow .ant-select-selection-search {
    right: 25px;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-search {
    position: absolute;
    top: 0;
    right: 11px;
    bottom: 0;
    left: 11px;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-item {
    position: relative;
    user-select: none;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    padding: 0;
    line-height: 30px;
    transition: all 0.3s;
  }

  .ant-select-single.ant-select-show-arrow .ant-select-selection-item,
  .ant-select-single.ant-select-show-arrow .ant-select-selection-placeholder {
    padding-right: ${themeGet('space.m')}px;
  }

  .ant-select-selection-item {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .ant-select:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search-input {
    margin: 0;
    padding: 0;
    background: 0 0;
    border: none;
    outline: none;
    appearance: none;
  }

  .ant-select-single:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search-input {
    height: 30px;
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector input {
    cursor: pointer;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    width: 100%;
  }

  .ant-pagination-jump-next,
  .ant-pagination-jump-prev {
    outline: 0;
  }

  .ant-pagination-jump-next .ant-pagination-item-container,
  .ant-pagination-jump-prev .ant-pagination-item-container {
    position: relative;
  }

  .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-link-icon,
  .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-link-icon {
    color: ${themeGet('colors.primary_500')};
    font-size: 12px;
    letter-spacing: -1px;
    opacity: 0;
    transition: all 0.2s;
  }

  .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-ellipsis,
  .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-ellipsis {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    margin: auto;
    color: rgba(0, 0, 0, 0.25);
    letter-spacing: 2px;
    text-align: center;
    text-indent: 0.13em;
    opacity: 1;
    transition: all 0.2s;
  }

  .ant-pagination-jump-next:focus .ant-pagination-item-link-icon,
  .ant-pagination-jump-next:hover .ant-pagination-item-link-icon,
  .ant-pagination-jump-prev:focus .ant-pagination-item-link-icon,
  .ant-pagination-jump-prev:hover .ant-pagination-item-link-icon {
    opacity: 1;
  }

  .ant-pagination-jump-next:focus .ant-pagination-item-ellipsis,
  .ant-pagination-jump-next:hover .ant-pagination-item-ellipsis,
  .ant-pagination-jump-prev:focus .ant-pagination-item-ellipsis,
  .ant-pagination-jump-prev:hover .ant-pagination-item-ellipsis {
    opacity: 0;
  }

  .ant-table-cell-ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: keep-all;
  }

  .ant-table-column-sorter-down.active,
  .ant-table-column-sorter-up.active {
    color: ${themeGet('colors.grey_600')};
  }

  .ant-spin-nested-loading {
    position: relative;
  }

  .ant-spin-spinning {
    position: static;
    display: inline-block;
    opacity: 1;
  }

  .ant-spin {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 999;
    background: rgba(224, 241, 243, 0.6);
  }

  .ant-spin-dot {
    display: inline-block;
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -10px;
  }

  .ant-table-content > table {
    min-height: 400px;
  }

  .ant-table-cell-ellipsis.ant-table-cell-fix-left-last,
  .ant-table-cell-ellipsis.ant-table-cell-fix-right-first {
    overflow: visible;
  }

  .ant-table-cell-ellipsis.ant-table-cell-fix-left-last .ant-table-cell-content,
  .ant-table-cell-ellipsis.ant-table-cell-fix-right-first .ant-table-cell-content {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-row-discount > .ant-table-cell-fix-left,
  .ant-table-row-discount > .ant-table-cell-fix-right {
    background-color: ${themeGet('colors.primary_75')};
  }

  .ant-table-row-discount {
    background-color: ${themeGet('colors.primary_75')};
  }
`;

function CustomTable({
  emptyLabel,
  onChangeTable,
  dataSource,
  columns,
  pagination: p,
  loading,
  onRow,
  rowClassName,
}) {
  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      onChangeTable({ pagination, filters, sorter });
    },
    [onChangeTable],
  );

  return (
    <StyledTable
      columns={columns}
      dataSource={dataSource}
      scroll={{ x: '100%' }}
      pagination={p}
      sticky
      showSorterTooltip={false}
      onChange={handleTableChange}
      locale={{
        emptyText: !loading ? <EmptyData label={emptyLabel} /> : <div />,
      }}
      loading={
        loading
          ? {
              indicator: (
                <WrapLoader>
                  <Loader width={50} />
                </WrapLoader>
              ),
            }
          : false
      }
      onRow={onRow}
      rowClassName={rowClassName}
    />
  );
}

export default CustomTable;
